"use client";

import { type ReactNode, useCallback, useEffect, useMemo } from "react";
import { usePathname } from "next/navigation";
import {
	ToastProvider as LibToastProvider,
	type ToastSeverity,
	useToastManager,
} from "@leabankab/component-library";

export interface ToastInput {
	severity: ToastSeverity;
	message: string;
}

const TOAST_ID = "flow-toast";

/** Errors and warnings wait for the customer; a timed error fails WCAG 2.2.1. */
const timeoutFor = (severity: ToastSeverity) =>
	severity === "error" || severity === "warning" ? 0 : undefined;

const CloseOnNavigation = () => {
	const pathname = usePathname();
	const { close } = useToastManager();

	useEffect(() => close(TOAST_ID), [pathname, close]);

	return null;
};

export const ToastProvider = ({ children }: { children: ReactNode }) => (
	<LibToastProvider limit={1}>
		<CloseOnNavigation />
		{children}
	</LibToastProvider>
);

/** One toast at a time: a new one replaces the one on screen. */
export const useToast = () => {
	const manager = useToastManager();

	const showToast = useCallback(
		({ severity, message }: ToastInput) => {
			manager.add({
				id: TOAST_ID,
				title: message,
				severity,
				timeout: timeoutFor(severity),
				// Base UI hides a high-priority toast from assistive tech until it has focus, which axe reports.
				priority: "low",
			});
		},
		[manager]
	);
	const closeToast = useCallback(() => manager.close(TOAST_ID), [manager]);

	return useMemo(() => ({ showToast, closeToast }), [showToast, closeToast]);
};
